import CommandInterceptor from 'diagram-js/lib/command/CommandInterceptor';
import EventBus from 'diagram-js/lib/core/EventBus';
import {ModelingUtil} from '@ispm/shared/bpmn-io/modeling-util';
import {NodeType} from '@ispm/shared/bpmn-io/node-type';
import {END_NODE_ID, HEAD_NODE_ID, subProcessType, taskType} from '@ispm-shared/modules/bpmn/diagram-element-terms';
import {EventType} from '@ispm/shared/bpmn-io/bpmn-js';
import {select, Store} from "@ngrx/store";
import {State} from "@ispm-client/modules/bpmn-modeler/store/state";
import {
  selectFloatedNodesByLayer,
  selectFloatedNodesMap
} from "@ispm-client/modules/bpmn-modeler/store/selectors/floating-nodes.selector";
import {filter, map, take} from "rxjs/operators";
import {selectNodeCreationInfoById, selectNodesCreationInfo} from "@ispm-client/modules/bpmn-modeler/store/selectors";


export class ImportParseCompleteBehavior extends CommandInterceptor{
  private rootElement;
  private importParseIsComplete: boolean = false;

  constructor(private eventBus: EventBus, private store$: Store<State>) {
    super(eventBus);
    this.eventBus.on(EventType.IMPORT_PARSE_COMPLETE, (event) => {
      const definitions = event.definitions;
      this.rootElement = definitions.rootElements[0];
      const headNode = this.rootElement.flowElements.find(moddleElement => moddleElement.id === HEAD_NODE_ID);
      this.markFloatingNodes();
      this.renameNode(headNode);
      this.markFirstLayer(headNode);
      // raise update_treatment_node_handler event and add it's handler
      const nodesIdsWithTrackingChange = this.getNodesIdsWithTrackingChange(event.elementsById);
      this.updateNodesAfterParse(nodesIdsWithTrackingChange);
      this.importParseIsComplete = true;
    });
  }

  private markFirstLayer(rootElement) {

    rootElement.markedToBeRendered = true;
    rootElement.neighborhoodID = HEAD_NODE_ID;
    if (ModelingUtil.getNodeType(rootElement) === NodeType.neighborhood_entry) {
      if (rootElement.outgoing && rootElement.outgoing.length > 0) {
        const outgoingConnection = rootElement.outgoing[0];
        const neighborhoodHeadNode = outgoingConnection.targetRef;
        this.renameNode(neighborhoodHeadNode);
        outgoingConnection.neighborhoodID = neighborhoodHeadNode.id;
        outgoingConnection.markedToBeRendered = false;
        this.markNextLayers(neighborhoodHeadNode, false, neighborhoodHeadNode.id);
      }
    } else {
      const outgoingConnections = rootElement.outgoing;
      let source;
      if (outgoingConnections && outgoingConnections.length > 0) {
        for (const conn of outgoingConnections) {
          conn.markedToBeRendered = true;
          conn.neighborhoodID = HEAD_NODE_ID;
          source = conn.targetRef;
          this.renameNode(source);
          if (source.id !== END_NODE_ID) {
            this.markFirstLayer(source);
          }
        }
      }
    }
  }


  private markNextLayers(rootElement, isMarked, neighborhoodHeadNodeID) {
    rootElement.markedToBeRendered = isMarked;
    rootElement.neighborhoodID = neighborhoodHeadNodeID;
    const outgoingConnections = rootElement.outgoing;
    let source;
    if (outgoingConnections && outgoingConnections.length > 0) {
      for (const conn of outgoingConnections) {
        conn.markedToBeRendered = isMarked;
        conn.neighborhoodID = neighborhoodHeadNodeID;
        source = conn.targetRef;
        this.renameNode(source);
        if (source.id !== END_NODE_ID) {
          this.markNextLayers(source, isMarked, neighborhoodHeadNodeID);
        }
      }
    }
  }

  private markFloatingNodes() {
    this.store$.pipe(select(selectFloatedNodesMap, take(1))).subscribe(floatedNodesMap => {
      if (!this.importParseIsComplete && floatedNodesMap && floatedNodesMap.size > 0) {
        floatedNodesMap.forEach((floatedNodes,neighborhoodID) => {
          this.store$.pipe(select(selectFloatedNodesByLayer(neighborhoodID)), take(1)).subscribe((floatingNodesIDs) => {
            if (floatingNodesIDs && floatingNodesIDs.length > 0) {
              floatingNodesIDs.forEach((floatingNodeID) => {
                const flowElement = this.rootElement.flowElements.find(element => element.id === floatingNodeID);
                if (flowElement) {
                  if (neighborhoodID === HEAD_NODE_ID) {
                    this.markFirstLayer(flowElement);

                  } else {
                    this.markNextLayers(flowElement, false, neighborhoodID);
                  }
                }
              });
            }
          });
        });
      }
    });
  }

  private updateNodesAfterParse(nodesIdsForUpdate) {
    this.store$.pipe(select(selectNodesCreationInfo), take(1)).toPromise().then((nodes) => {
      if (this.importParseIsComplete && nodes && Object.keys(nodes).length > 0) {
        const nodeIds = Object.keys(nodes).filter((nodeId) => !!nodes[nodeId]);
        nodeIds.forEach(nodeId => {
          if (nodes[nodeId].type === NodeType.treatment || nodesIdsForUpdate.includes(nodeId)) {
            this.eventBus.fire(EventType.AFTER_PARSE_COMPLETE, {
              nodeId: nodeId,
              nodeCreationResult: nodes[nodeId],
              nodeType: nodes[nodeId].type
            });
          }
        });
      }
    });
  }

  private getNodesIdsWithTrackingChange(elementsById){
    let nodesIdsWithTrackingChange = [];
    for (let id in elementsById) {
      const element = elementsById[id];
      const trackingChange = ModelingUtil.getTrackingChange(element);
      if (trackingChange && (element.$type == taskType || element.$type == subProcessType) && (trackingChange.type === 'added' || trackingChange.type === 'updated')) {
        nodesIdsWithTrackingChange.push(element.id);
      }
    }
    return nodesIdsWithTrackingChange;
  }

  private renameNode(node) {
    this.store$.pipe(
      select(selectNodeCreationInfoById([node.id])),
      filter(nodes => !!nodes),
      map(nodes => nodes[0]),
      take(1)
  ).subscribe( nodeInfo => {
      node.name = nodeInfo &&
                  nodeInfo.data &&
                  nodeInfo.data.name ||
                  node.name;
        });
  }
}

ImportParseCompleteBehavior.$inject = [...CommandInterceptor.$inject, 'store$'];
